import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  mailboxCreateSchema,
  mailboxListSchema,
  mailboxQuotaUpdateSchema,
  mailboxSchema,
  type Mailbox,
  type MailboxCreate,
} from '@bekebi/contracts/api';
import { ApiError, apiRequest } from '../../api/client';

/**
 * Data access for the Mailboxes area — the existing endpoints only:
 *   GET   /api/mail/mailboxes            the Mail Server's mailboxes (mail.mailboxes.view)
 *   POST  /api/mail/mailboxes            add a mailbox (mail.mailboxes.manage)
 *   PATCH /api/mail/mailboxes/:address   change a mailbox's size (mail.mailboxes.manage)
 * The API answers 503 `mail_server_not_connected` while no Mail Server connector exists, and
 * 503 `mail_server_unavailable` / 502 `mail_server_error` when a connector cannot be reached or
 * answers unexpectedly. The quota rules (1 GB minimum, 5 GB for ICT) are enforced by the API
 * before the Mail Server is contacted; a write counts as done only when the API confirms it,
 * which it does only after the Mail Server did. Nothing is updated optimistically.
 */
export const mailboxKeys = { list: ['mailboxes', 'list'] as const };

/**
 * How often the open Mailboxes page re-reads the list from the Mail Server, so changes made directly
 * in Mailu appear without a reload. Mailu offers no change notification (no webhook or event API),
 * so this is polling: at most one request per interval per open page, only while the page is
 * visible (paused in a background tab), never overlapping (a refresh still in flight is reused,
 * and the API shares one Mailu listing between concurrent requests).
 */
export const MAILBOX_REFRESH_INTERVAL_MS = 60_000;

/** What the page shows, decided only from the API's own answer. */
export type MailboxesState =
  | { kind: 'loading' }
  | { kind: 'not_connected' }
  | { kind: 'unreachable' }
  | { kind: 'error'; message: string }
  | {
      kind: 'connected';
      mailboxes: Mailbox[];
      /** When the Mail Server last confirmed this list (ms since the epoch). */
      updatedAt: number;
      /** The latest refresh failed: the list shown is the last confirmed one, not current. */
      refreshFailed: boolean;
    };

const UNREACHABLE_CODES = new Set(['mail_server_unavailable', 'mail_server_error']);

/**
 * The Mail Server's mailboxes. Every page that uses this re-reads Mailu when it opens (Mailu is the
 * source of truth; nothing is kept as authoritative in the portal). `poll` (the list page only)
 * also refreshes every MAILBOX_REFRESH_INTERVAL_MS while visible and when the window regains focus.
 */
export function useMailboxes(options: { poll?: boolean } = {}) {
  const poll = options.poll ?? false;
  const query = useQuery({
    queryKey: mailboxKeys.list,
    // The signal lets a superseded read (a fresh read after a confirmed change) or one still running
    // when the page closes be aborted, rather than finishing unused in the background.
    queryFn: async ({ signal }) =>
      (await apiRequest('/mail/mailboxes', { schema: mailboxListSchema, signal })).items,
    retry: false,
    staleTime: 0,
    refetchOnMount: 'always',
    refetchInterval: poll ? MAILBOX_REFRESH_INTERVAL_MS : false,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: poll,
  });

  let state: MailboxesState;
  if (query.isPending) state = { kind: 'loading' };
  else if (query.data !== undefined) {
    // A confirmed list exists: a failed refresh keeps it, clearly marked, instead of hiding it.
    state = {
      kind: 'connected',
      mailboxes: query.data,
      updatedAt: query.dataUpdatedAt,
      refreshFailed: query.isError,
    };
  } else {
    const code = query.error instanceof ApiError ? query.error.code : null;
    if (code === 'mail_server_not_connected') state = { kind: 'not_connected' };
    else if (code && UNREACHABLE_CODES.has(code)) state = { kind: 'unreachable' };
    else
      state = { kind: 'error', message: query.error?.message ?? 'Mailboxes could not be loaded.' };
  }

  return {
    state,
    /** A manual refresh; joins a refresh already in flight instead of starting a second one. */
    refresh: () => query.refetch({ cancelRefetch: false }),
    isFetching: query.isFetching,
  };
}

export function useCreateMailbox() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: MailboxCreate) =>
      apiRequest('/mail/mailboxes', {
        method: 'POST',
        body: mailboxCreateSchema.parse(input),
        schema: mailboxSchema,
      }),
    onSettled: async () => queryClient.invalidateQueries({ queryKey: mailboxKeys.list }),
  });
}

export function useChangeMailboxQuota() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ address, quotaBytes }: { address: string; quotaBytes: number }) =>
      apiRequest(`/mail/mailboxes/${encodeURIComponent(address)}`, {
        method: 'PATCH',
        body: mailboxQuotaUpdateSchema.parse({ quotaBytes }),
        schema: mailboxSchema,
      }),
    // Re-read from the Mail Server either way: it is the authority on every mailbox's size.
    onSettled: async () => queryClient.invalidateQueries({ queryKey: mailboxKeys.list }),
  });
}
